import { Container, Header } from "components/Page"
import { Route, Switch, useParams } from "react-router-dom"
import { css, cx } from "@emotion/css"
import { styledTabClasses, styledTabsProps } from "components/Tabs"

import { AgencyClientDocuments } from "../../AgencyClients/Details/AgencyClientDocuments"
import { AgencyClientProducts } from "../../AgencyClients/Details/AgencyClientProducts"
import AgencyClientTaxes from "../../AgencyClients/Details/AgencyClientTaxes"
import { AuthorizedRoute } from "components/AuthorizedRoute"
import Badge from "components/Badge"
import Breadcrumbs from "components/Breadcrumbs"
import { ColorMap } from "utils/color"
import { MarketClientProfile } from "./Profile"
import Surface from "components/Surface"
import { TabLink } from "components/Link"
import { Tabs } from "@material-ui/core"
import { fetchClient } from "api/clients"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useQuery } from "react-query"

const tabs = [
  "profile" /* t(agency.client.profile) */,
  "products" /* t(agency.client.products) */,
  "taxes" /* t(agency.client.taxes)*/,
  "documents" /* t(agency.client.documents) */,
]

export function MarketClientDetails() {
  const { t } = useI18n()
  const { id, tab = "profile" } = useParams<{ id: string; tab: string }>()
  const { hasAuthority } = useAuthenticated()

  const { data: client } = useQuery(["market-client", id], fetchClient, {
    enabled: !!id,
  })

  const handleTabs = (tabs: string[]) => {
    let activeTabs = tabs
    const hideDocuments =
      !hasAuthority(["AUTH_VIEW_CLIENT_DOCUMENT"]) ||
      hasAuthority(["AUTH_MANAGE_CLIENT_DOCUMENT"])
    const hideTaxes =
      !hasAuthority(["AUTH_VIEW_CLIENT_TAX"]) ||
      !hasAuthority(["AUTH_EDIT_CLIENT_TAX"])

    if (hideDocuments && hideTaxes) {
      activeTabs = tabs.filter((tb) => tb !== "documents" && tb !== "taxes")
    } else if (hideDocuments) {
      activeTabs = tabs.filter((tb) => tb !== "documents")
    } else if (hideTaxes) {
      activeTabs = tabs.filter((tb) => tb !== "taxes")
    } else {
      activeTabs = tabs
    }
    return activeTabs
  }

  return (
    <Container>
      <Breadcrumbs
        current={client?.name}
        items={[
          {
            backLink: `${t("nav.admin")} / ${t("nav.market.clients")}`,
            to: "/market-clients",
          },
        ]}
      />
      <Header
        label={client?.name}
        badge={
          <Badge color={ColorMap[client?.status as any]}>
            {client?.status}
          </Badge>
        }
      />
      <Surface flexDirection="row" className={styles.surface}>
        <Tabs
          value={tab}
          {...styledTabsProps}
          classes={{
            ...styledTabsProps.classes,
            flexContainer: cx(styledTabsProps?.classes?.flexContainer),
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {handleTabs(tabs).map((tb) => (
            <TabLink
              key={tb}
              to={`/market-clients/${id}/${tb}/${id}`}
              classes={styledTabClasses}
              value={tb}
              label={t(`market.client.${tb}`)}
            />
          ))}
        </Tabs>
        <div>
          <Switch>
            <AuthorizedRoute
              allowed={["AUTH_VIEW_CLIENT"]}
              component={MarketClientProfile}
              path="/market-clients/:id/profile"
            />
            <AuthorizedRoute
              allowed={["AUTH_VIEW_PRODUCTS", "AUTH_VIEW_CLIENT"]}
              component={AgencyClientProducts}
              path="/market-clients/:id/products"
            />
            <AuthorizedRoute
              allowed={[
                "AUTH_VIEW_CLIENT_DOCUMENT",
                "AUTH_MANAGE_CLIENT_DOCUMENT",
              ]}
              component={AgencyClientDocuments}
              path="/market-clients/:id/documents"
            />
            <AuthorizedRoute allowed={["AUTH_VIEW_CLIENT_TAX"]}>
              <AgencyClientTaxes clientId={id} />
            </AuthorizedRoute>
            <Route component={MarketClientProfile} />
          </Switch>
        </div>
      </Surface>
    </Container>
  )
}

const styles = {
  surface: css`
    padding: var(--spacing-xl) var(--spacing-m) var(--spacing-m);
  `,
}
